/**
 * PainCycleChart — iter1060 — болката като ЛИНИЯ върху фазите на цикъла.
 * Хоризонталната ос е ден от цикъла, не дата: записи от различни месеци
 * падат един върху друг и модел (или липсата му) става видим.
 */
import React from "react";
import {
  CartesianGrid, Line, ComposedChart, ReferenceArea, ReferenceLine,
  ResponsiveContainer, Scatter, Tooltip, XAxis, YAxis,
} from "recharts";

const PHASE_ORDER = ["menstrual", "follicular", "ovulation", "luteal"];

export const PainCycleChart = ({ chart, isDark }) => {
  if (!chart?.has_points) {
    return (
      <div className={`p-3 rounded-xl text-[10px] leading-relaxed ${isDark ? "bg-slate-800/50" : "bg-slate-100"}`}
           data-testid="pain-cycle-chart-empty">
        Графиката се появява след първия запис с известен ден от цикъла.
        Нужна е дата на последния менструация в хормоналния близнак — без нея
        записите нямат къде да легнат.
      </div>
    );
  }

  const grid = isDark ? "#1e293b" : "#e2e8f0";
  const axis = isDark ? "#94a3b8" : "#475569";
  const series = chart.series || [];
  const points = (chart.points || []).map((p) => ({ ...p, dot: p.intensity }));
  const maxDay = Math.max(chart.cycle_length || 28, ...series.map((s) => s.cycle_day));

  return (
    <div data-testid="pain-cycle-chart">
      <div className="flex flex-wrap gap-1.5 mb-1.5" data-testid="pain-chart-legend">
        {PHASE_ORDER.filter((p) => (chart.bands || []).some((b) => b.phase_id === p)).map((p) => {
          const band = chart.bands.find((b) => b.phase_id === p);
          return (
            <span key={p} className="text-[9px] px-1.5 py-0.5 rounded-md flex items-center gap-1"
                  style={{ background: `${band.color}22`, color: band.color }}
                  data-testid={`pain-chart-band-${p}`}>
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: band.color }} />
              {band.label_bg}{band.avg != null ? ` · ср. ${band.avg}` : ""}
            </span>
          );
        })}
      </div>

      <div style={{ width: "100%", height: 200 }}>
        <ResponsiveContainer>
          <ComposedChart data={series} margin={{ top: 6, right: 8, left: -22, bottom: 14 }}>
            {(chart.bands || []).map((b) => (
              <ReferenceArea key={b.phase_id} x1={b.from} x2={b.to} y1={0} y2={10}
                             fill={b.color} fillOpacity={0.1} stroke="none" />
            ))}
            {(chart.bands || []).filter((b) => b.avg != null).map((b) => (
              <ReferenceLine key={`avg-${b.phase_id}`} y={b.avg} stroke={b.color}
                             strokeDasharray="4 4" strokeOpacity={0.7} />
            ))}
            <CartesianGrid stroke={grid} strokeDasharray="2 4" vertical={false} />
            <XAxis dataKey="cycle_day" type="number" domain={[1, maxDay]}
                   tick={{ fontSize: 9, fill: axis }} tickLine={false}
                   label={{ value: "ден от цикъла", position: "insideBottom",
                            offset: -10, fontSize: 9, fill: axis }} />
            <YAxis domain={[0, 10]} ticks={[0, 2, 4, 6, 8, 10]}
                   tick={{ fontSize: 9, fill: axis }} tickLine={false} />
            <Tooltip
              contentStyle={{
                background: isDark ? "#0f172a" : "#fff",
                border: `1px solid ${grid}`, borderRadius: 10, fontSize: 11,
              }}
              formatter={(v, n) => (n === "intensity" ? [v, "сила"] : null)}
              labelFormatter={(l) => `Ден ${l} от цикъла`} />
            <Line type="monotone" dataKey="intensity" stroke="#ec4899" strokeWidth={2}
                  dot={{ r: 3, fill: "#ec4899" }} activeDot={{ r: 5 }} name="intensity" />
            <Scatter data={points} dataKey="dot" fill="#ec4899" fillOpacity={0.35}
                     tooltipType="none" legendType="none" />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <p className="text-[10px] opacity-70 leading-relaxed mt-1" data-testid="pain-chart-axis-note">
        {chart.axis_note_bg}
      </p>
      <p className="text-[10px] opacity-60 leading-relaxed mt-1" data-testid="pain-chart-read-note">
        {chart.read_bg}
      </p>
    </div>
  );
};

export default PainCycleChart;
